/**
 * 统一接口请求服务
 * 支持环境切换、错误处理、请求拦截等
 */
import { API_BASE_URL, API_TIMEOUT } from '../config/env'
import logger from '../utils/logger'
import { showAppToast } from '../utils/toast'
import { useWalletStore } from '@/store/wallet';
import { useLoginUiStore } from '@/store/loginUi';
import { getJouleDecimals, isJouleJ18, setRuntimeJouleDecimals } from '../utils/money';
import { orderRequestStarted, orderResponseReceived, setOrderExperienceReporter, trackOrderRequestTiming } from './orderExperience';
import {configureConnectionDiagnostics,connectionOrderRequest,connectionApiResult} from './connectionDiagnostics';

configureConnectionDiagnostics(()=>({wallet:getCurrentWalletAddress(),token:getToken()}));

setOrderExperienceReporter((report, wallet) => {
  const token=getToken();
  if(!token || token.startsWith('mock-') || getCurrentWalletAddress().toLowerCase().trim()!==wallet)return;
  // Telemetry never refreshes auth, opens login, retries an order or blocks rendering.
  uni.request({
    url:`${API_BASE_URL}${import.meta.env.DEV?'':'/api'}/v1/order-experience`,
    method:'POST',data:report,timeout:3000,
    header:{Authorization:`Bearer ${token}`,'Content-Type':'application/json'},
    fail:()=>{},
  });
},()=>getCurrentWalletAddress());

/**
 * J18 写请求必须带 X-Joule-Decimals: 18（见 docs/joule-j18-migration.md）。
 * 仅当运行时确认为 18 时附加，避免向仍为 J8 的联调环境误发头。
 */
const JOULE_PRECISION_WRITE_RE =
  /^\/v1\/(orders(\/preview|\/submission)?|exchanges(\/quote)?|wealth\/(quote|subscriptions)|insurance\/(sessions|claims)|insurance-wallet\/transfers|copy-trade-wallet\/transfers|nodes\/[^/]+\/(quote|purchase)|copy-trade\/(follows|traders\/(register|settings)))(\?|$)/;

const needsJouleDecimalsHeader = (method: string, url: string): boolean => {
  const m = String(method).toUpperCase();
  const path = String(url || '').split('?')[0];
  if (m === 'POST') return JOULE_PRECISION_WRITE_RE.test(path);
  // 达人设置 PATCH 可能带 minFollowerBalance（JOULE 原子整数）
  if (m === 'PATCH' && path === '/v1/copy-trade/traders/settings') return true;
  // 跟单关系配置 PATCH 可能带止盈止损原子金额
  if (m === 'PATCH' && /^\/v1\/copy-trade\/follows\/[^/]+$/.test(path)) return true;
  return false;
};

const syncJouleDecimalsFromHeaders = (header: unknown) => {
  if (!header || typeof header !== 'object') return;
  const h = header as Record<string, string>;
  const raw =
    h['X-Joule-Decimals'] ??
    h['x-joule-decimals'] ??
    h['X-JOULE-DECIMALS'];
  if (raw == null || raw === '') return;
  const n = Number(raw);
  if (Number.isFinite(n)) setRuntimeJouleDecimals(n);
};
// 请求配置接口
export interface RequestOptions {
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  data?: any
  header?: Record<string, string>
  timeout?: number
  showLoading?: boolean
  /**
   * 是否弹出接口业务/HTTP 错误 toast。
   * 默认按白名单：登录鉴权、绑定、下单、充值、兑换、提现等写操作提示；
   * 列表/行情等展示类请求静默。也可显式传 true/false 覆盖。
   */
  showError?: boolean
  skipAuth?: boolean
  /** Maps to Idempotency-Key header */
  idempotencyKey?: string
  /** Maps to X-Client-Order-ID header */
  clientOrderId?: string
  /** Maps to X-Order-Submission-Mode（direct-v1） */
  orderSubmissionMode?: string
  /** Maps to X-Order-Submission-Token */
  orderSubmissionToken?: string
  /**
   * 401 刷新成功后允许重放本请求的次数。
   * 未设置：保持原行为（刷新后重放）；
   * 0：不重放（正式下单）；1：允许一次（submission resolve / 订单详情）。
   */
  authRetryLimit?: number
  /** 内部：已重放次数 */
  _authRetryCount?: number
  /** 请求发出前校验仍为该钱包，否则中止 */
  expectedWallet?: string
}

// 响应接口
interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  code?: number | string;
  requestId?: string;
}

/** Structured Business API error */
export class ApiError extends Error {
  code?: string
  requestId?: string
  statusCode?: number
  details?: Record<string, unknown>

  constructor(
    message: string,
    options?: {
      code?: string
      requestId?: string
      statusCode?: number
      details?: Record<string, unknown>
    }
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = options?.code
    this.requestId = options?.requestId
    this.statusCode = options?.statusCode
    this.details = options?.details
  }
}

// 请求等待刷新时的回调；刷新结束必须全部放行或失败，不能挂起
type QueuedAuthRetry = {
  run: () => void
  fail: (err: ApiError) => void
}
let requestQueue: QueuedAuthRetry[] = []
let isRefreshing = false
let refreshInFlight: Promise<RefreshResult> | null = null
/** 换钱包 / 退出后递增，丢弃旧刷新结果 */
let authEpoch = 0
let refreshToastAt = 0
/** 401 且刷新凭证失效后 / 连续 401 熔断：阻断后续鉴权请求 */
let authBlocked = false
/**
 * 「refresh 已成功（含短路）但业务仍 401」的连续次数。
 * transient / 明确鉴权失败不走此计数；业务 2xx 或 saveToken 会清零。
 */
let consecutive401Count = 0
const CONSECUTIVE_401_LIMIT = 10
const REFRESH_LEAD_MS = 60 * 1000
const REFRESH_INTERVAL_MS = 5 * 60 * 1000
const REFRESH_UNAVAILABLE_MESSAGE = '登录续期暂不可用，点击重试'
const REFRESH_ORIGIN_MESSAGE = '登录环境配置错误，请联系支持'
const RELOGIN_MESSAGE = '登录已过期，请重新登录'

type RefreshResult = {
  ok: boolean
  reason: 'ok' | 'transient' | 'cookie_missing' | 'invalid' | 'origin' | 'stale'
  accessToken?: string
  expiresIn?: number
  address?: string
}

const AUTH_BROADCAST = 'panta-auth-session'
let authChannel: BroadcastChannel | null = null

const bumpAuthEpoch = () => {
  authEpoch += 1
  if (proactiveTimer) clearTimeout(proactiveTimer);
  proactiveTimer = null;
  proactiveRetryDelay = 0;
}

const postAuthBroadcast = (type: 'refreshed' | 'cleared') => {
  try {
    authChannel?.postMessage({
      type,
      epoch: authEpoch,
      wallet: getCurrentWalletAddress(),
    })
  } catch {
    // ignore
  }
}
let reLoginPromptedAt = 0
let sessionPromptShown = false

const resetConsecutive401 = () => {
  consecutive401Count = 0
}

/** 关掉可能已打开的登录底栏，避免与会话确认弹框叠层 */
const dismissLoginSheet = () => {
  try {
    useLoginUiStore().closeLoginSheet()
  } catch {
    /* ignore */
  }
  try {
    uni.$emit('closeLoginModal')
  } catch {
    /* ignore */
  }
}

/** 连续 401 / 刷新凭证失效：清本地会话、阻断请求并弹确认框 */
const tripSessionAuthGate = () => {
  authBlocked = true
  isRefreshing = false
  // 作废进行中的 refresh，避免 clear 之后又 saveToken / syncWalletSession 把会话写回
  bumpAuthEpoch()
  if (proactiveTimer) {
    clearTimeout(proactiveTimer)
    proactiveTimer = null
  }

  const queued = requestQueue
  requestQueue = []
  const err = new ApiError(RELOGIN_MESSAGE, { code: 'UNAUTHORIZED', statusCode: 401 })
  queued.forEach((item) => {
    try {
      item.fail(err)
    } catch {
      /* ignore */
    }
  })

  // 会话已不可用：立刻清本地态，避免 UI 仍显示已登录却全线 401
  clearLocalAuth()

  // 先收起登录底栏，再弹会话确认（forceReLogin / 页面 triggerLogin 可能已打开底栏）
  dismissLoginSheet()

  if (!sessionPromptShown) {
    sessionPromptShown = true
    reLoginPromptedAt = Date.now()
    try {
      useLoginUiStore().openSessionPrompt()
    } catch {
      /* pinia 未就绪 */
    }
  }
}

// Token 统一存储键前缀
const TOKEN_STORAGE_KEY_PREFIX = 'axi_auth_';
// 当前绑定的钱包地址存储键
const CURRENT_WALLET_KEY = 'axi_current_wallet';

// Token 存储结构
interface TokenStorage {
  token: string;           // 访问令牌（登录和刷新共用）
  expiryTime?: number;     // 过期时间戳（毫秒）
  walletAddress?: string;  // 关联的钱包地址
}

// 获取当前绑定的钱包地址
const getCurrentWalletAddress = (): string => {
  try {
    return localStorage.getItem(CURRENT_WALLET_KEY) || '';
  } catch (error) {
    return '';
  }
}

// 设置当前绑定的钱包地址
const setCurrentWalletAddress = (address: string) => {
  try {
    const next = address ? address.toLowerCase() : '';
    const prev = getCurrentWalletAddress();
    if (next !== prev) {
      bumpAuthEpoch();
      // 切号：旧账户的 401 熔断 / 会话框不得带到新钱包
      resetConsecutive401();
      authBlocked = false;
      sessionPromptShown = false;
      try {
        useLoginUiStore().closeSessionPrompt();
      } catch {
        /* ignore */
      }
    }
    if (next) {
      localStorage.setItem(CURRENT_WALLET_KEY, next);
    } else {
      localStorage.removeItem(CURRENT_WALLET_KEY);
    }
  } catch (error) {
    // Set wallet address failed
  }
}

// 根据钱包地址生成存储键
const getTokenStorageKey = (address?: string): string => {
  const walletAddress = address || getCurrentWalletAddress();
  if (!walletAddress) {
    return TOKEN_STORAGE_KEY_PREFIX + 'default';
  }
  return TOKEN_STORAGE_KEY_PREFIX + walletAddress.toLowerCase();
}

// 获取 token 存储对象
const getTokenStorage = (address?: string): TokenStorage => {
  try {
    const key = getTokenStorageKey(address);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    // Token storage read failed
  }
  return { token: '' };
}

// 保存 token 存储对象
const saveTokenStorage = (data: TokenStorage, address?: string) => {
  try {
    const key = getTokenStorageKey(address);
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    // Token storage save failed
  }
}

// 清除 token 存储
const clearTokenStorage = (address?: string) => {
  try {
    bumpAuthEpoch();
    const key = getTokenStorageKey(address);
    localStorage.removeItem(key);
  } catch (error) {
    // Token storage clear failed
  }
}

// 获取 token
const getToken = (address?: string): string => {
  return getTokenStorage(address).token || '';
}

// 获取指定钱包地址的 token（用于检查是否需要重新登录）
const getTokenForWallet = (walletAddress: string): string => {
  if (!walletAddress) return '';
  return getTokenStorage(walletAddress).token || '';
}

// 检查 token 是否已过期
const isTokenExpired = (): boolean => {
  const { expiryTime } = getTokenStorage();
  if (!expiryTime) return false;
  return Date.now() >= expiryTime;
}

// 检查指定钱包地址的 token 是否已过期
const isTokenExpiredForWallet = (walletAddress: string): boolean => {
  if (!walletAddress) return true;
  const { token, expiryTime } = getTokenStorage(walletAddress);
  if (!token) return true;
  if (!expiryTime) return false; // 没有过期时间，认为未过期
  return Date.now() >= expiryTime;
}

// 到期前 60 秒主动刷新（后台定时器不可靠，业务 401 仍会兜底）
const shouldRefreshToken = (): boolean => {
  const { token, expiryTime } = getTokenStorage();
  if (!token || !expiryTime) return false;
  const timeUntilExpiry = expiryTime - Date.now();
  return timeUntilExpiry > 0 && timeUntilExpiry <= REFRESH_LEAD_MS;
}

// 只同步网站会话，不把 Cookie 续期误当成钱包 provider 已连接。
const syncWalletSession = (token: string, address: string) => {
  // 会话门禁已触发时禁止回写，避免 in-flight refresh 短路把已清会话写回
  if (authBlocked) return;
  const walletStore = useWalletStore();
  walletStore.address = address || null;
  walletStore.accessToken = token;
  walletStore.authSyncing = false;
};

// 保存 token（登录和刷新共用）
const saveToken = (token: string, expiresIn?: number, walletAddress?: string) => {
  try {
    authBlocked = false
    sessionPromptShown = false
    resetConsecutive401()
    try {
      useLoginUiStore().closeSessionPrompt()
    } catch {
      /* ignore */
    }
    const address = walletAddress || getCurrentWalletAddress();
    const current = getTokenStorage(address);
    const newStorage: TokenStorage = {
      token: token,
      expiryTime: expiresIn ? Date.now() + expiresIn * 1000 : current.expiryTime,
      walletAddress: address
    };
    saveTokenStorage(newStorage, address);
    
    // 更新当前绑定的钱包地址
    if (address) {
      setCurrentWalletAddress(address);
    }
    
    // 同步更新 walletStore 的内存状态
    syncWalletSession(token, address);
    scheduleProactiveRefresh();
    postAuthBroadcast('refreshed');
  } catch (error) {
    // Save token failed
  }
}

/** 仅清本地登录态，不触发 disconnect 整页 reload */
const clearLocalAuth = (opts?: { broadcast?: boolean }) => {
  try {
    clearTokenStorage();
    const walletStore = useWalletStore();
    walletStore.accessToken = null;
    walletStore.authSyncing = false;
    if (opts?.broadcast !== false) {
      postAuthBroadcast('cleared');
    }
  } catch {
    // ignore
  }
}

/**
 * 无 token / 需立刻登录：清本地态并打开登录底栏。
 * 若已在会话确认弹框流程中，不再叠底栏（由用户点「重新登录」处理）。
 */
const forceReLogin = (message = RELOGIN_MESSAGE) => {
  if (sessionPromptShown) {
    authBlocked = true;
    isRefreshing = false;
    clearLocalAuth();
    const queued = requestQueue;
    requestQueue = [];
    const err = new ApiError(message, { code: 'UNAUTHORIZED', statusCode: 401 });
    queued.forEach((item) => {
      try {
        item.fail(err);
      } catch {
        /* ignore */
      }
    });
    return;
  }

  const first = !authBlocked;
  authBlocked = true;
  isRefreshing = false;
  clearLocalAuth();

  const queued = requestQueue;
  requestQueue = [];

  if (first || Date.now() - reLoginPromptedAt > 2500) {
    reLoginPromptedAt = Date.now();
    try {
      useLoginUiStore().open();
    } catch {
      /* pinia 未就绪 */
    }
    try {
      uni.$emit('openLoginModal');
    } catch {
      /* ignore */
    }
    try {
      uni.showToast({
        title: message,
        icon: 'none',
        duration: 2000,
      });
    } catch {
      /* ignore */
    }
  }

  const err = new ApiError(message, { code: 'UNAUTHORIZED', statusCode: 401 });
  queued.forEach((item) => {
    try {
      item.fail(err);
    } catch {
      /* ignore */
    }
  });
}

// 清除 token（主动退出等场景仍走 disconnect）
const clearToken = () => {
  try {
    authBlocked = true;
    const walletStore = useWalletStore();
    walletStore.disconnect();
  } catch (error) {
    // Clear token failed
  }
}

// 显示加载提示（只显示动画，不显示文字）
const showLoading = () => {
  uni.showLoading({
    title: '',
    mask: true
  })
}

// 隐藏加载提示
const hideLoading = () => {
  uni.hideLoading()
}

/**
 * 从响应对象中提取错误消息
 * 支持多种错误格式:
 * 1. { code: "INSUFFICIENT_FUNDS", message: "...", requestId: "..." }  // Business API
 * 2. { success: false, error: { code: "SYS_999", message: "内部服务器错误" } }
 * 3. { detail: "无效的认证凭证" }
 * 4. { detail: { success: false, error: { message: "获取币种信息失败" } } }
 * 5. { message: "错误信息" }
 */
const extractErrorMessage = (obj: unknown): string => {
  if (!obj) return 'REQUEST_FAILED';
  
  if (typeof obj === 'string') {
    // Check if it's an HTML error page (e.g., 502 Bad Gateway)
    if (obj.includes('<!DOCTYPE') || obj.includes('<html') || obj.includes('<HTML')) {
      return 'SERVER_UNAVAILABLE';
    }
    // If string is too long, simplify
    if (obj.length > 100) {
      return 'REQUEST_FAILED';
    }
    return obj;
  }
  
  if (typeof obj === 'object') {
    const data = obj as Record<string, any>;

    // Business API: { code, message, requestId }
    if (typeof data.message === 'string' && data.message && (data.code || data.requestId)) {
      return data.message;
    }
    
    // 直接的 error.message
    if (data.error?.message) {
      return data.error.message;
    }
    
    // 嵌套的 detail 对象
    if (data.detail && typeof data.detail === 'object') {
      const detail = data.detail as Record<string, any>;
      if (detail.error?.message) {
        return detail.error.message;
      }
      if (detail.message) {
        return detail.message;
      }
    }
    
    // detail 是字符串
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    
    // Direct message
    if (data.message) {
      return data.message;
    }
  }
  
  return 'REQUEST_FAILED';
};

const extractApiErrorMeta = (
  obj: unknown
): {
  code?: string
  requestId?: string
  details?: Record<string, unknown>
} => {
  if (!obj || typeof obj !== 'object') return {};
  const data = obj as Record<string, any>;
  const code =
    (typeof data.code === 'string' && data.code) ||
    (typeof data.error?.code === 'string' && data.error.code) ||
    undefined;
  const requestId =
    (typeof data.requestId === 'string' && data.requestId) ||
    undefined;
  const detailsRaw = data.details ?? data.error?.details;
  const details: Record<string, unknown> | undefined =
    detailsRaw && typeof detailsRaw === 'object' && !Array.isArray(detailsRaw)
      ? { ...(detailsRaw as Record<string, unknown>) }
      : undefined;
  // 跟单 resume 阈值错误等：weekNetPnl 可能在 body 顶层
  if (data.weekNetPnl != null && String(data.weekNetPnl).trim()) {
    const merged = details ? { ...details } : {};
    if (merged.weekNetPnl == null) merged.weekNetPnl = data.weekNetPnl;
    return { code, requestId, details: merged };
  }
  return { code, requestId, details };
};

const shortenErrorText = (raw: string): string => {
  const text = String(raw || '').trim();
  if (!text) return '请求失败';
  // 网关 404 会带回整段路由，不适合直接展示
  if (/^Route\s+\w+:/i.test(text) || text === 'Not Found') {
    return '接口暂不可用，请稍后再试';
  }
  return text;
};

/**
 * 用户主动操作类写接口：报错需要提示。
 * 列表/余额/行情等只读展示接口不在此列，默认静默。
 */
const isActionErrorToastUrl = (method: string, url: string): boolean => {
  const m = String(method || 'GET').toUpperCase();
  const path = String(url || '').split('?')[0];
  if (m === 'POST') {
    // 登录 / 鉴权
    if (path === '/v1/auth/wallet/challenge') return true;
    if (path === '/v1/auth/wallet/verify') return true;
    if (path === '/v1/auth/logout') return true;
    // 邀请绑定：由业务侧自行 toast 成功/失败，不弹接口原文
    if (path === '/v1/network/bind') return false;
    // 下单
    if (path === '/v1/orders') return true;
    if (path === '/v1/orders/preview') return true;
    if (path === '/v1/orders/submission') return true;
    // AI 绑定 / 预测
    if (path === '/v1/ai/predictions') return true;
    // 充值 / 兑换 / 提现 / 划转
    if (path === '/v1/deposits/claim') return true;
    if (path === '/v1/exchanges') return true;
    if (path === '/v1/exchanges/quote') return true;
    if (path === '/v1/withdrawals') return true;
    if (path === '/v1/insurance-wallet/transfers') return true;
    return false;
  }
  if (m === 'PATCH' && path === '/v1/insurance/preferences') return true;
  if (m === 'DELETE' && path.startsWith('/v1/insurance/sessions/')) return true;
  if (m === 'PUT' && path.startsWith('/v1/network/team/') && path.endsWith('/note')) {
    return true;
  }
  return false;
};

const resolveNeedErrorToast = (
  method: string,
  url: string,
  showErrorOpt: boolean | undefined
): boolean => {
  if (typeof showErrorOpt === 'boolean') return showErrorOpt;
  return isActionErrorToastUrl(method, url);
};

const showError = (message: unknown) => {
  const errorText = shortenErrorText(extractErrorMessage(message));
  showAppToast({ title: errorText, icon: 'error', duration: 2500 });
};

const buildUniRequestExtras = (): Record<string, unknown> => {
  // H5: send cookies for refresh/logout; other platforms ignore unknown fields safely
  return {
    withCredentials: true,
    credentials: 'include',
  };
};

const parseAuthTokenPayload = (
  result: any
): { accessToken?: string; expiresIn?: number; address?: string } => {
  if (!result || typeof result !== 'object') return {};
  // 新文档：与 wallet/verify 同构（accessToken / expiresIn / address）
  if (result.accessToken) {
    return {
      accessToken: result.accessToken,
      expiresIn: result.expiresIn,
      address: result.address,
    };
  }
  return {};
};

const readRefreshCode = (data: unknown): string => {
  if (!data || typeof data !== 'object') return '';
  const body = data as Record<string, any>;
  return String(body.code || body.error?.code || '');
};

const toastRefresh = (title: string) => {
  if (!title) return;
  if (Date.now() - refreshToastAt < 2500) return;
  refreshToastAt = Date.now();
  try {
    uni.showToast({ title, icon: 'none', duration: 2500 });
  } catch {
    // ignore
  }
};

const failQueue = (err: ApiError) => {
  const queued = requestQueue;
  requestQueue = [];
  queued.forEach((item) => {
    try {
      item.fail(err);
    } catch {
      // ignore
    }
  });
};

const classifyRefreshFailure = (status: number, data: unknown): RefreshResult => {
  const code = readRefreshCode(data);
  if (status === 401 && code === 'AUTH_REFRESH_COOKIE_MISSING') {
    return { ok: false, reason: 'cookie_missing' };
  }
  if (status === 401 || code === 'AUTH_REFRESH_INVALID') {
    return { ok: false, reason: 'invalid' };
  }
  if (status === 403 || code === 'AUTH_ORIGIN_INVALID') {
    return { ok: false, reason: 'origin' };
  }
  return { ok: false, reason: 'transient' };
};

const refreshError = (result: RefreshResult): ApiError => {
  // 本地 access 仍有效：错误给调用方，文案保持中性，避免冒充「登录异常 / 凭据缺失」
  const softKeepAlive = sessionStillValid();
  if (result.reason === 'cookie_missing') {
    // 不对用户暴露 Cookie/凭据文案；双失效时与过期同文案
    return new ApiError(softKeepAlive ? '请稍后重试' : RELOGIN_MESSAGE, {
      code: 'AUTH_REFRESH_COOKIE_MISSING',
      statusCode: 401,
    });
  }
  if (result.reason === 'origin') {
    return new ApiError(
      softKeepAlive ? '请稍后重试' : REFRESH_ORIGIN_MESSAGE,
      {
        code: 'AUTH_ORIGIN_INVALID',
        statusCode: 403,
      }
    );
  }
  if (result.reason === 'transient') {
    return new ApiError(
      softKeepAlive ? '请稍后重试' : REFRESH_UNAVAILABLE_MESSAGE,
      {
        code: 'AUTH_UNAVAILABLE',
        statusCode: 503,
      }
    );
  }
  if (result.reason === 'stale') {
    return new ApiError('Wallet switched during refresh', {
      code: 'WALLET_SWITCHED',
      statusCode: 0,
    });
  }
  return new ApiError(softKeepAlive ? '请稍后重试' : RELOGIN_MESSAGE, {
    code: 'AUTH_REFRESH_INVALID',
    statusCode: 401,
  });
};

const sessionStillValid = (): boolean => {
  const token = getToken();
  return !!token && !isTokenExpired();
};

/**
 * refresh 鉴权失败是否应踢会话。
 * 规则：本地 access 仍在效期内 → 只当 refresh 抖动/跨站 Cookie 未带上，不判整会话无效；
 * 仅 access 已过期（或无 token）且 refresh 也失败时，才双失效踢人。
 */
const shouldTripOnRefreshAuthFailure = (
  reason: RefreshResult['reason']
): reason is 'invalid' | 'cookie_missing' => {
  if (reason !== 'invalid' && reason !== 'cookie_missing') return false;
  return !sessionStillValid();
};

/** access 仍有效时 refresh 失败：推迟到 access 临近过期再试，避免频繁 refresh 误踢 */
const deferRefreshUntilAccessNearExpiry = () => {
  const { expiryTime } = getTokenStorage();
  if (!expiryTime || authBlocked) return;
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  const delay = Math.max(1000, expiryTime - Date.now() - REFRESH_LEAD_MS);
  proactiveTimer = setTimeout(() => {
    proactiveTimer = null;
    void refreshIfDue().then(handleBackgroundRefresh);
  }, delay);
};

/**
 * 全站唯一刷新。拦截器、钱包恢复、手动续期共用同一个 Promise。
 * 不打印 Cookie / Access Token。超时、离线、503、429 不清登录态。
 */
const refreshSession = (rejectedToken?: string, force = false): Promise<RefreshResult> => {
  if (refreshInFlight) return refreshInFlight;

  const epochAtStart = authEpoch;
  const walletAtStart = getCurrentWalletAddress();
  isRefreshing = true;
  if (rejectedToken || !getToken() || isTokenExpired()) {
    try { useWalletStore().authSyncing = true; } catch { /* store not mounted */ }
  }

  const run = async (): Promise<RefreshResult> => {
    const stored = getTokenStorage();
    if (
      authEpoch === epochAtStart &&
      !authBlocked &&
      !force &&
      stored.token &&
      stored.token !== rejectedToken &&
      stored.expiryTime &&
      stored.expiryTime - Date.now() > REFRESH_LEAD_MS &&
      (!walletAtStart ||
        !stored.walletAddress ||
        stored.walletAddress.toLowerCase() === walletAtStart.toLowerCase())
    ) {
      try {
        syncWalletSession(stored.token, walletAtStart);
      } catch {
        // ignore
      }
      // 短路不 resetConsecutive401：业务仍 401 时由 await 后的熔断计数承接；
      // 真正换发新 token（saveToken）或业务 2xx 才会清零。
      return { ok: true, reason: 'ok', accessToken: stored.token };
    }

    if (authEpoch !== epochAtStart) {
      return { ok: false, reason: 'stale' };
    }

    const isDevServer = import.meta.env.DEV;
    const refreshUrl = isDevServer
      ? `${API_BASE_URL}/v1/auth/refresh`
      : `${API_BASE_URL}/api/v1/auth/refresh`;

    let status = 0;
    let data: unknown = null;
    try {
      const response = await uni.request({
        url: refreshUrl,
        method: 'POST',
        header: {
          'Content-Type': 'application/json',
        },
        data: {},
        timeout: API_TIMEOUT,
        ...buildUniRequestExtras(),
      } as UniApp.RequestOptions);
      status = response.statusCode || 0;
      data = response.data;
    } catch {
      return { ok: false, reason: 'transient' };
    }

    if (authEpoch !== epochAtStart || getCurrentWalletAddress() !== walletAtStart) {
      return { ok: false, reason: 'stale' };
    }

    const parsed = parseAuthTokenPayload(data);
    if (status >= 200 && status < 300 && parsed.accessToken) {
      const responseAddress = parsed.address ? String(parsed.address).toLowerCase().trim() : '';
      const want = (walletAtStart || '').toLowerCase().trim();
      // Cookie 会话必须带 address，且与当前钱包一致；否则会把旧账户 JWT 误绑到新地址
      if (!responseAddress || (want && responseAddress !== want)) {
        return { ok: false, reason: 'stale' };
      }
      // trip 可能发生在 await 之后、写回之前：再检一次，避免 saveToken 清掉门禁
      if (authEpoch !== epochAtStart || authBlocked) {
        return { ok: false, reason: 'stale' };
      }
      saveToken(parsed.accessToken, parsed.expiresIn, responseAddress);
      return {
        ok: true,
        reason: 'ok',
        accessToken: parsed.accessToken,
        expiresIn: parsed.expiresIn,
        address: responseAddress,
      };
    }

    if (status === 0 || status === 408 || status === 429 || status >= 500) {
      return { ok: false, reason: 'transient' };
    }
    return classifyRefreshFailure(status, data);
  };

  const locked = async (): Promise<RefreshResult> => {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (locks?.request) {
      return locks.request('panta-auth-refresh', run);
    }
    return run();
  };

  const task = (async (): Promise<RefreshResult> => {
    let result: RefreshResult;
    try {
      result = await locked();
    } catch {
      result = { ok: false, reason: 'transient' };
    }
    const queued = requestQueue;
    requestQueue = [];
    if (refreshInFlight === task) refreshInFlight = null;
    isRefreshing = false;
    if (authEpoch === epochAtStart) {
      if (result.ok) {
        try { useWalletStore().authSyncing = false; } catch { /* store not mounted */ }
        proactiveRetryDelay = 0;
        scheduleProactiveRefresh();
      } else if (result.reason === 'transient' && (getToken() || walletAtStart) && !authBlocked) {
        proactiveRetryDelay = Math.min(30000, proactiveRetryDelay ? proactiveRetryDelay * 2 : 2000);
        if (proactiveTimer) clearTimeout(proactiveTimer);
        proactiveTimer = setTimeout(() => {
          proactiveTimer = null;
          void refreshSession(rejectedToken, force).then(handleBackgroundRefresh);
        }, proactiveRetryDelay);
      } else {
        // stale / invalid / cookie_missing：结束续期态，避免切号后一直卡在 syncing
        try { useWalletStore().authSyncing = false; } catch { /* store not mounted */ }
      }
    }
    if (result.ok) {
      queued.forEach((item) => {
        try {
          item.run();
        } catch {
          // ignore
        }
      });
    } else {
      const err = refreshError(result);
      queued.forEach((item) => {
        try {
          item.fail(err);
        } catch {
          // ignore
        }
      });
    }
    return result;
  })();

  refreshInFlight = task;
  return task;
};

/** 到期前 60 秒或已过期时刷新；没有 token 时不因钱包已连接而刷新 */
const refreshIfDue = (): Promise<RefreshResult> => {
  if (!getToken()) {
    // 无 token：不发 refresh，也不当成「登录过期」（避免无 401 却弹会话框）
    return Promise.resolve({ ok: false, reason: 'stale' });
  }
  if (isTokenExpired() || shouldRefreshToken()) {
    return refreshSession();
  }
  return Promise.resolve({ ok: true, reason: 'ok' });
};

let proactiveTimer: ReturnType<typeof setTimeout> | null = null;
let proactiveRetryDelay = 0;

const handleBackgroundRefresh = (result: RefreshResult) => {
  // stale/transient：不弹窗；鉴权失败时仅「access 已失效 + refresh 也失败」才踢人
  if (result.ok || result.reason === 'stale' || result.reason === 'transient') return;
  if (result.reason === 'origin') return;
  if (result.reason !== 'invalid' && result.reason !== 'cookie_missing') return;

  if (!shouldTripOnRefreshAuthFailure(result.reason)) {
    // access 仍在效期：常见于跨站 Cookie 未带上，保留会话，避开频繁 refresh
    deferRefreshUntilAccessNearExpiry();
    return;
  }
  tripSessionAuthGate();
};

const scheduleProactiveRefresh = () => {
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  if (authBlocked) return;
  const { token, expiryTime } = getTokenStorage();
  if (!token || !expiryTime) return;
  const delay = Math.max(1000, Math.min(REFRESH_INTERVAL_MS, expiryTime - Date.now() - REFRESH_LEAD_MS));
  proactiveTimer = setTimeout(() => {
    proactiveTimer = null;
    void refreshSession(undefined, true).then(handleBackgroundRefresh);
  }, delay);
};

/**
 * 统一请求方法
 */
export const ensureAuthenticated = async (expectedWallet = getCurrentWalletAddress()): Promise<void> => {
  const epoch = authEpoch;
  const matches = () => epoch === authEpoch && !!expectedWallet &&
    expectedWallet.toLowerCase() === getCurrentWalletAddress().toLowerCase();
  if (!matches()) throw refreshError({ ok: false, reason: 'stale' });
  if (authBlocked) {
    if (!sessionPromptShown) tripSessionAuthGate();
    throw refreshError({ ok: false, reason: 'invalid' });
  }
  if (!getToken() && !useWalletStore().authSyncing) {
    // 本地无 token：走登录底栏，不冒充「登录已过期」会话框
    forceReLogin();
    throw refreshError({ ok: false, reason: 'invalid' });
  }
  const result = refreshInFlight ? await refreshInFlight : await (getToken() ? refreshIfDue() : refreshSession());
  if (!matches()) throw refreshError({ ok: false, reason: 'stale' });
  if (!result.ok) {
    // 双失效才踢：access 已过期且 refresh 也鉴权失败
    if (shouldTripOnRefreshAuthFailure(result.reason)) {
      tripSessionAuthGate();
    } else if (result.reason === 'cookie_missing' || result.reason === 'invalid') {
      deferRefreshUntilAccessNearExpiry();
    }
    throw refreshError(result);
  }
};

export const request = async <T = any>(options: RequestOptions): Promise<T> => {
  // Authenticate BEFORE sending user work, not by replaying a rejected order.
  // Background previews remain passive and must not open login or refresh.
  const background = options.header?.['X-Order-Preview-Purpose'] === 'prefetch';
  if (!options.skipAuth && !background && options.method === 'POST' &&
      /^\/v1\/(orders(?:\/preview|\/submission)?|ai\/predictions)$/.test(options.url)) {
    const wallet = options.expectedWallet || getCurrentWalletAddress();
    await ensureAuthenticated(wallet);
    options = { ...options, expectedWallet: wallet };
  }
  return new Promise((resolve, reject) => {
    const {
      url,
      method = 'GET',
      data,
      header = {},
      timeout = API_TIMEOUT,
      showLoading: needLoading = false,
      showError: showErrorOpt,
      skipAuth = false,
      idempotencyKey,
      clientOrderId,
      orderSubmissionMode,
      orderSubmissionToken,
      authRetryLimit,
      _authRetryCount = 0,
      expectedWallet,
    } = options

    const needError = resolveNeedErrorToast(method, url, showErrorOpt);
    const backgroundPreview = url === '/v1/orders/preview' && header['X-Order-Preview-Purpose'] === 'prefetch';

    if (needLoading) {
      showLoading()
    }

    if (expectedWallet) {
      const current = (getCurrentWalletAddress() || '').toLowerCase().trim()
      const want = String(expectedWallet).toLowerCase().trim()
      if (!want || !current || current !== want) {
        if (needLoading) hideLoading()
        reject(
          new ApiError('Wallet switched during request', {
            code: 'WALLET_SWITCHED',
            statusCode: 0,
          })
        )
        return
      }
    }

    // 公开接口（无需 Bearer）；refresh/logout 依赖 Cookie
    const publicUrls = [
      '/v1/auth/config',
      '/v1/auth/wallet/challenge',
      '/v1/auth/wallet/verify',
      '/v1/auth/refresh',
      '/v1/auth/logout',
      '/v1/config/platform-coin',
      '/v1/markets',
      '/v1/market/status',
      '/v1/quotes/',
      '/v1/klines/',
      '/v1/insurance/rules',
      // 公告横幅：未登录可拉；登录时仍带 JWT 以拿到 read
      '/v1/announcements/active',
      // 跟单：读配置、榜单、按地址搜达人不用登录
      '/v1/copy-trade/settings',
      '/v1/copy-trade/leaderboard',
      '/v1/copy-trade/traders/search',
    ];

    const token = getToken();
    // 公开达人结算：/traders/:wallet/settled-orders（勿把 /traders/me/* 放行）
    const pathOnly = String(url || '').split('?')[0];
    const isPublicSettledOrders =
      /^\/v1\/copy-trade\/traders\/0x[a-fA-F0-9]{40}\/settled-orders$/.test(pathOnly);
    const isPublic =
      isPublicSettledOrders || publicUrls.some((publicUrl) => url.startsWith(publicUrl));

    // 会话已熔断：停止后续鉴权请求；只保留会话确认弹框，勿再狂弹登录层
    if (authBlocked && !isPublic && !skipAuth) {
      if (needLoading) hideLoading();
      if (!backgroundPreview && !sessionPromptShown) {
        tripSessionAuthGate();
      }
      reject(
        new ApiError(RELOGIN_MESSAGE, {
          code: 'UNAUTHORIZED',
          statusCode: 401,
        })
      );
      return;
    }

    // 如果接口需要 token 但 token 不存在，则直接拒绝请求
    if (!isPublic && !token && !skipAuth) {
      if (useWalletStore().authSyncing) {
        if (needLoading) hideLoading();
        reject(refreshError({ ok: false, reason: 'transient' }));
        return;
      }
      logger.warn(`[Request] ⚠️ 接口需要认证但 token 不存在: ${url}`);
      if (needLoading) {
        hideLoading();
      }
      if (!backgroundPreview) forceReLogin();
      reject(
        new ApiError(RELOGIN_MESSAGE, {
          code: 'UNAUTHORIZED',
          statusCode: 401,
        })
      );
      return;
    }

    const requestHeader: Record<string, string> = {
      ...header
    };

    // uni 会给 POST 自动添加 JSON 请求头；空体会被后端拒绝解析。
    // 预约、入场及 refresh/logout 等无参数 POST 统一发送有效 JSON。
    const requestData = method === 'POST' ? data ?? {} : data;

    if (method !== 'GET' && requestData !== undefined && requestData !== null) {
      requestHeader['Content-Type'] = 'application/json';
    }

    // challenge/verify/config 等公开鉴权接口不要带旧 Bearer（本地常残留 mock-wallet-*）
    const skipBearer =
      skipAuth ||
      url.startsWith('/v1/auth/wallet/') ||
      url.startsWith('/v1/auth/config') ||
      url.startsWith('/v1/auth/refresh') ||
      url.startsWith('/v1/auth/logout');

    if (token && !skipBearer && !String(token).startsWith('mock-')) {
      requestHeader['Authorization'] = `Bearer ${token}`;
    }

    if (idempotencyKey) {
      requestHeader['Idempotency-Key'] = idempotencyKey;
    }
    if (clientOrderId) {
      requestHeader['X-Client-Order-ID'] = clientOrderId;
    }
    if (orderSubmissionMode) {
      requestHeader['X-Order-Submission-Mode'] = orderSubmissionMode;
    }
    if (orderSubmissionToken) {
      requestHeader['X-Order-Submission-Token'] = orderSubmissionToken;
    }

    // J18 资金写请求：金额已按 18 位换算时声明精度（与金额单位必须一致）
    if (
      needsJouleDecimalsHeader(method, url) &&
      isJouleJ18() &&
      !requestHeader['X-Joule-Decimals'] &&
      !requestHeader['x-joule-decimals']
    ) {
      requestHeader['X-Joule-Decimals'] = String(getJouleDecimals());
    }

    const safeHeader = { ...requestHeader };
    if (safeHeader.Authorization) safeHeader.Authorization = 'Bearer ***';
    const hideAuthBody = /\/v1\/auth\/(refresh|logout|wallet\/(verify|challenge))/.test(url);
    if (!hideAuthBody) {
      logger.debug(`[Request] ${method} ${url}`, { data: requestData, header: safeHeader });
    }

    // 构建完整URL：开发环境直接用API_BASE_URL（已经是/api），生产环境需要拼接/api前缀
    const isDevServer = import.meta.env.DEV
    const fullUrl = isDevServer 
      ? `${API_BASE_URL}${url}` // 开发环境：/api + /v1/xxx
      : `${API_BASE_URL}/api${url}` // 生产环境：https://domain.com + /api + /v1/xxx

    logger.debug(`[Request] Full URL: ${fullUrl}`)

    const replayLimit =
      typeof authRetryLimit === 'number' ? authRetryLimit : idempotencyKey ? 0 : 1;
    const sensitiveAuth = /\/v1\/auth\/(refresh|logout|wallet\/(verify|challenge))/.test(url);

    // 对于 GET 请求，将 data 转换为查询参数
    const requestConfig: Record<string, any> = {
      url: fullUrl,
      method: method as any,
      header: requestHeader,
      timeout,
      ...buildUniRequestExtras(),
    };

    // GET 请求使用查询参数，其他请求使用 body
    if (method === 'GET' && requestData) {
      // 将 data 对象转换为查询字符串
      const queryParams = new URLSearchParams();
      Object.keys(requestData).forEach(key => {
        if (requestData[key] !== undefined && requestData[key] !== null) {
          queryParams.append(key, String(requestData[key]));
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        requestConfig.url = `${fullUrl}?${queryString}`;
      }
    } else if (requestData !== undefined) {
      requestConfig.data = requestData;
    }

    const experienceRequestId=orderRequestStarted(url,method,requestData,clientOrderId);
    const connectionResult=connectionOrderRequest(url,method,clientOrderId||(requestData as {clientOrderId?:string}|undefined)?.clientOrderId);
    const experienceWallet=getCurrentWalletAddress();
    if(experienceRequestId)requestHeader['X-Request-ID']=experienceRequestId;
    const finishTiming = trackOrderRequestTiming(url, method, requestData, backgroundPreview);
    uni.request({
      ...requestConfig,
      success: async (response: UniApp.RequestSuccessCallbackResult) => {
        finishTiming(response.statusCode, response.header);
        const experienceBody=(response.data as any)?.data ?? response.data;
        connectionResult(response.statusCode,experienceBody);
        connectionApiResult(url,response.statusCode);
        orderResponseReceived(url,method,experienceBody,response.statusCode,experienceRequestId,experienceWallet);
        if (needLoading) {
          hideLoading()
        }

        const raw = response.data as unknown;
        const result = raw as ApiResponse<T>;

        syncJouleDecimalsFromHeaders(response.header);

        if (!sensitiveAuth) {
          logger.debug(`[Response] ${method} ${url}`, result);
        }

        const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

        if (isSuccess) {
          // 鉴权业务成功才清连续 401（refresh 单独路径，不在此）
          if (!isPublic && !skipAuth && !url.startsWith('/v1/auth/refresh')) {
            resetConsecutive401();
          }
          // 204 No Content (e.g. logout)
          if (response.statusCode === 204 || raw === '' || raw == null) {
            resolve(undefined as T);
            return;
          }

          // 检查业务层面的 success 字段
          if (result && typeof result === 'object' && result.success === true && typeof result.data !== 'undefined') {
            resolve(result.data as T);
          } else if (result && typeof result === 'object' && result.success === false) {
            // 业务失败，提取错误信息并 reject
            const errorMessage = extractErrorMessage(result);
            const meta = extractApiErrorMeta(result);
            if (needError) {
              showError(errorMessage);
            }
            reject(new ApiError(errorMessage, {
              code: meta.code,
              requestId: meta.requestId,
              statusCode: response.statusCode,
              details: meta.details,
            }));
          } else {
            // 没有 success 字段（新 Business API 直接返回 body），直接返回结果
            resolve(result as unknown as T);
          }
          return;
        }

        if (response.statusCode === 401) {
          // Speculative previews cannot open login UI, refresh auth or replay user work.
          if (backgroundPreview) {
            reject(new ApiError(RELOGIN_MESSAGE, { code: 'UNAUTHORIZED', statusCode: 401 }));
            return;
          }
          if (url.startsWith('/v1/auth/refresh') || url.startsWith('/v1/auth/logout')) {
            reject(
              new ApiError(RELOGIN_MESSAGE, {
                code: 'UNAUTHORIZED',
                statusCode: 401,
              })
            );
            return;
          }

          if (authBlocked) {
            reject(
              new ApiError(RELOGIN_MESSAGE, {
                code: 'UNAUTHORIZED',
                statusCode: 401,
              })
            );
            return;
          }

          const canReplay = _authRetryCount < replayLimit;
          const replay = () =>
            request({
              ...options,
              _authRetryCount: _authRetryCount + 1,
            }).then(resolve).catch(reject);

          // 续期进行中：只排队，不计入熔断（避免切号/过期瞬间并行 401 误杀）
          if (isRefreshing || refreshInFlight) {
            requestQueue.push({
              run: () => canReplay ? replay() : reject(new ApiError('登录已恢复，请重新确认下单', {
                code: 'AUTH_RENEWED_NOT_REPLAYED', statusCode: 401,
              })),
              fail: (err) => reject(err),
            });
            return;
          }

          // 先 await refresh，再决定 toast / 熔断；禁止在结果未知时踢人
          try { useWalletStore().authSyncing = true; } catch { /* store not mounted */ }
          const result = await refreshSession(token);

          if (result.ok) {
            // refresh 成功（含短路复用本地 token）后业务仍 401：才累计熔断
            consecutive401Count += 1;
            if (consecutive401Count >= CONSECUTIVE_401_LIMIT) {
              tripSessionAuthGate();
              reject(
                new ApiError(RELOGIN_MESSAGE, {
                  code: 'UNAUTHORIZED',
                  statusCode: 401,
                })
              );
              return;
            }
            if (canReplay) {
              replay();
            } else {
              reject(
                new ApiError('登录已恢复，请重新确认下单', {
                  code: 'AUTH_RENEWED_NOT_REPLAYED',
                  statusCode: 401,
                })
              );
            }
            return;
          }

          // 未双失效：静默保留本地会话，不 toast「凭据异常 / 续期失败」类文案
          if (result.reason === 'cookie_missing' || result.reason === 'invalid') {
            if (shouldTripOnRefreshAuthFailure(result.reason)) {
              tripSessionAuthGate();
            } else {
              deferRefreshUntilAccessNearExpiry();
            }
          } else if (
            (result.reason === 'transient' || result.reason === 'origin') &&
            !sessionStillValid()
          ) {
            // access 已无本地效期才提示续期/环境问题；仍有效则交给调用方错误即可
            toastRefresh(
              result.reason === 'origin' ? REFRESH_ORIGIN_MESSAGE : REFRESH_UNAVAILABLE_MESSAGE
            );
          }
          // stale：不弹会话框，由钱包层引导重登

          reject(refreshError(result));
          return;
        }

        // 使用统一的错误提取函数（含 { code, message, requestId, details }）
        const errorMessage = extractErrorMessage(result);
        const meta = extractApiErrorMeta(result);
        const precisionMismatch =
          response.statusCode === 409 ||
          meta.code === 'AMOUNT_PRECISION_MISMATCH';
        const displayMessage = precisionMismatch
          ? errorMessage ||
            'Amount precision mismatch (Joule decimals). Refresh and retry.'
          : errorMessage;
        if (needError) {
          showError(displayMessage)
        }
        reject(new ApiError(displayMessage, {
          code: meta.code || (precisionMismatch ? 'AMOUNT_PRECISION_MISMATCH' : undefined),
          requestId: meta.requestId,
          statusCode: response.statusCode,
          details: meta.details,
        }))
      },
      fail: (error) => {
        finishTiming(0);
        connectionApiResult(url,0);
        if (needLoading) {
          hideLoading()
        }

        logger.error(`[Request Failed] ${method} ${url}`, error)

        // 传输层失败：默认静默；登录/下单等操作类接口仍提示，避免 DApp 登录「点了没反应」
        const errorMessage = error.errMsg || (error as any).message || 'net error'
        if (needError) {
          const isTimeout = /timeout/i.test(errorMessage)
          showError(isTimeout ? 'Request timeout' : 'Network error')
        }

        reject(
          new ApiError(errorMessage, {
            code: /timeout/i.test(errorMessage) ? 'TIMEOUT' : 'NETWORK',
            statusCode: 0,
          })
        );
      }
    } as any)
  })
}

// 便捷方法
export const get = <T = any>(url: string, params?: any, options?: Partial<RequestOptions>) => {
  return request<T>({
    url,
    method: 'GET',
    data: params,
    ...options
  })
}

export const post = <T = any>(url: string, data?: any, options?: Partial<RequestOptions>) => {
  return request<T>({
    url,
    method: 'POST',
    data,
    ...options
  })
}

export const put = <T = any>(url: string, data?: any, options?: Partial<RequestOptions>) => {
  return request<T>({
    url,
    method: 'PUT',
    data,
    ...options
  })
}

export const del = <T = any>(url: string, data?: any, options?: Partial<RequestOptions>) => {
  return request<T>({
    url,
    method: 'DELETE',
    data,
    ...options
  })
}

/** 用户主动点登录时解除 401 门禁，允许 challenge / verify */
const clearAuthGate = () => {
  authBlocked = false;
  isRefreshing = false;
  reLoginPromptedAt = 0;
  sessionPromptShown = false;
  resetConsecutive401();
};

/**
 * 会话弹框「重新登录」：静默清本地登录态并打开登录弹窗。
 * 不走 disconnect / 整页刷新；钱包连接保留便于直接重签。
 */
export const beginSessionRelogin = () => {
  authBlocked = true;
  isRefreshing = false;
  sessionPromptShown = true;
  resetConsecutive401();
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  clearLocalAuth();
  try {
    useLoginUiStore().closeSessionPrompt();
  } catch {
    /* ignore */
  }
  try {
    useLoginUiStore().open();
  } catch {
    /* ignore */
  }
  try {
    uni.$emit('openLoginModal');
  } catch {
    /* ignore */
  }
};

// 导出 token 相关方法供 authService 使用
export {
  saveToken,
  getToken,
  clearTokenStorage,
  getTokenForWallet,
  setCurrentWalletAddress,
  getCurrentWalletAddress,
  isTokenExpiredForWallet,
  shouldRefreshToken,
  clearAuthGate,
  refreshSession,
  refreshIfDue,
  bumpAuthEpoch,
};

const bindAuthRefreshListeners = () => {
  if (typeof BroadcastChannel !== 'undefined' && !authChannel) {
    try {
      authChannel = new BroadcastChannel(AUTH_BROADCAST);
      authChannel.onmessage = (ev: MessageEvent) => {
        const data = ev?.data as { type?: string; wallet?: string } | undefined;
        if (!data?.type) return;
        const current = getCurrentWalletAddress();
        if (data.wallet && current && data.wallet !== current) return;
        if (data.type === 'refreshed') {
          const token = getToken();
          if (!token) return;
          try {
            syncWalletSession(token, current);
            scheduleProactiveRefresh();
          } catch {
            // ignore
          }
          return;
        }
        if (data.type === 'cleared') {
          if (sessionStillValid()) return;
          clearLocalAuth({ broadcast: false });
        }
      };
    } catch {
      authChannel = null;
    }
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        if (!authBlocked && (getToken() || (getCurrentWalletAddress() && useWalletStore().authSyncing))) {
          const pending = useWalletStore().authSyncing ? refreshSession(undefined, true) : refreshIfDue();
          void pending.then(handleBackgroundRefresh);
        }
      }
    });
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      if (!authBlocked && (getToken() || (getCurrentWalletAddress() && useWalletStore().authSyncing))) {
        const pending = useWalletStore().authSyncing ? refreshSession(undefined, true) : refreshIfDue();
        void pending.then(handleBackgroundRefresh);
      }
    });
  }

  if (getToken()) scheduleProactiveRefresh();
};

bindAuthRefreshListeners();

export default request
